import { addDoc, collection, doc, getDocs, query, serverTimestamp, where } from "@firebase/firestore";
import { dismissLoadingToast, showErrorToast, showLoadingToast, showSuccessToast, updateLoadingToast } from "app/components/ShowToast";
import axios from "axios";
import db from "services/firebase";

interface GitLabNamespace {
  name?: string;
  full_path?: string;
  id?: number;
  web_url?: string;
  kind?: string;
}

interface Project {
  name: string;
  html_url: string;
  private: boolean;
  description?: string | null;
  created_at?: string;
  last_activity_at: string;
  pushed_at: string;
  stargazers_count: number;
  forks_count: number;
  open_issues_count: number;
  watchers_count: number;
  language?: string | null;
  default_branch: string;
  is_repo: boolean;
  repo_name: string;
  web_url: string;
  platform: string;
  visibility: string;
  size?: number;
  star_count: number;
  watchers?: number;
  forks: number;
  open_issues: number;
  namespace?: GitLabNamespace;
  owner?: {
    login: string;
    id: number;
    html_url: string;
    type: string;
  };
  tenantId?: string;
  uid?: string;
  createdAt?: any;
}

interface GitLabIntegrationResponse {
  success: boolean;
  projects?: Project[];
  error?: string;
}

interface MFAEnforcementResponse {
  success: boolean;
  mfaRequired?: boolean;
  message?: string;
  error?: string;
}

interface UserMFAResponse {
    success: boolean;
    users?: {
      username: string;
      name: string | null;
      '2faEnabled': boolean;
      role: string | null;
      id: number;
      html_url: string;
    }[];
    error?: string;
  }

interface GitLabError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
  message?: string;
}

interface CheckProjectsResponse {
  success: boolean;
  newProjects?: Project[];
  error?: string;
}

export const createGitLabIntegration = (accessToken: string) => {

  if (!accessToken || typeof accessToken !== 'string' || accessToken.trim() === '') {
    throw new Error('A valid access token is required for GitLab integration');
  }

  const axiosInstance = axios.create({
    baseURL: `https://gitlab.com/api/v4`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });

  const getUserFriendlyErrorMessage = (error: unknown): string => {
    const gitlabError = error as GitLabError;
    if (gitlabError.response?.status === 403) {
      return "Access forbidden. Please check your access token permissions.";
    } else if (gitlabError.response?.status === 401) {
      return "Invalid access token. Please reconnect to GitLab.";
    } else if (gitlabError.response?.status === 404) {
      return "GitLab group not found. Please check the group name.";
    } else if (gitlabError.response?.status === 429) {
      return "GitLab API rate limit exceeded. Please try again later.";
    }
    return (
      gitlabError.response?.data?.message ||
      gitlabError.message ||
      "An unexpected error occurred."
    );
  };

  
  const checkUser2FA = async (groupId: number): Promise<UserMFAResponse> => {
    try {
      const membersResponse = await axiosInstance.get(`/groups/${groupId}/members`);
      const members = membersResponse.data;
  
      const results = await Promise.all(
        members.map(async (member: any) => {
          try {
            const userResponse = await axiosInstance.get(`/users/${member.id}`);
            const user = userResponse.data;
  
            const roleMap: { [key: number]: string } = {
              10: 'Guest',
              20: 'Reporter',
              30: 'Developer',
              40: 'Maintainer',
              50: 'Owner',
            };
            const role = roleMap[member.access_level] || 'Unknown';
  
            return {
              username: user.username,
              name: user.name || user.username,
              MfaEnabled: user.two_factor_enabled ?? false,
              role,
              id: user.id,
              html_url: user.web_url || `https://gitlab.com/${user.username}`,
            };
          } catch (error: unknown) {
            console.error(`Error fetching details for user ${member.username}:`, error);
            return {
              username: member.username || `user_${member.id}`,
              name: member.username || `user_${member.id}`,
              '2faEnabled': false,
              role: null,
              id: member.id,
              html_url: `https://gitlab.com/${member.username}`,
            };
          }
        })
      );
  
      return { success: true, users: results };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  const checkVulnerabilities = async (projectPath: string) => {
    try {
      const encodedProjectPath = encodeURIComponent(projectPath);
      const vulnerabilitiesResponse = await axiosInstance.get(
        `/projects/${encodedProjectPath}/vulnerabilities?state=detected`
      );
      const vulnerabilities = vulnerabilitiesResponse.data;
      const slaDays = 7;
      const now = Date.now();
      const results = vulnerabilities.map((vuln: any) => {
        const createdDate = new Date(vuln.created_at).getTime();
        const diffDays = Math.ceil((now - createdDate) / (1000 * 60 * 60 * 24));
        return {
          vulnerability: vuln.name || 'Unnamed vulnerability',
          withinSLA: diffDays <= slaDays,
          daysOpen: diffDays,
        };
      });
      return { success: true, vulnerabilities: results };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  const checkBranchProtection = async (projectPath: string) => {
    try {
      const encodedProjectPath = encodeURIComponent(projectPath);
      const protectedBranchesResponse = await axiosInstance.get(
        `/projects/${encodedProjectPath}/protected_branches`
      );
      const protectedBranches = protectedBranchesResponse.data.map((branch: any) => ({
        branch: branch.name,
        protected: true,
        rules: {
          push_access_levels: branch.push_access_levels,
          merge_access_levels: branch.merge_access_levels,
        },
      }));
      return { success: true, branches: protectedBranches };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  const checkCodeReviewRequirements = async (projectPath: string) => {
    try {
      const encodedProjectPath = encodeURIComponent(projectPath);
      const approvalsResponse = await axiosInstance.get(
        `/projects/${encodedProjectPath}/approvals`
      );
      const approvals = approvalsResponse.data;
      return {
        success: true,
        approvals: {
          approvals_required: approvals.approvals_before_merge,
        },
      };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  const getAllGitLabProjects = async (): Promise<GitLabIntegrationResponse> => {
    try {
      let allProjects: any[] = [];
      let page = 1;
      const perPage = 100;

      while (true) {
        const projectsResponse = await axiosInstance.get('/projects', {
          params: {
            per_page: perPage,
            page: page,
            membership: true,
            archived: false,
            sort: 'asc',
            order_by: 'name',
            simple: false,
            statistics: false,
            with_custom_attributes: false,
          },
        });

        let projects = projectsResponse.data;
        projects = projects.filter((project: any) =>
          project.permissions?.project_access?.access_level >= 40
        );

        allProjects = allProjects.concat(projects);

        const totalPages = parseInt(projectsResponse.headers["x-total-pages"] || "1", 10);
        if (page >= totalPages) {
          break;
        }
        page++;
      }

      let groupsPage = 1;
      const groups: any[] = [];

      while (true) {
        const groupsResponse = await axiosInstance.get('/groups', {
          params: {
            per_page: perPage,
            page: groupsPage,
            min_access_level: 40,
            all_available: false,
          },
        });

        const fetchedGroups = groupsResponse.data;
        groups.push(...fetchedGroups);

        const totalGroupPages = parseInt(groupsResponse.headers["x-total-pages"] || "1", 10);
        if (groupsPage >= totalGroupPages) {
          break;
        }
        groupsPage++;
      }

      for (const group of groups) {
        let groupProjectsPage = 1;

        while (true) {
          const groupProjectsResponse = await axiosInstance.get(`/groups/${group.id}/projects`, {
            params: {
              per_page: perPage,
              page: groupProjectsPage,
              archived: false,
              sort: 'asc',
              order_by: 'name',
              simple: false,
              include_subgroups: true,
            },
          });

          const groupProjects = groupProjectsResponse.data;
          const filteredGroupProjects = groupProjects.filter((project: any) =>
            project.permissions?.project_access?.access_level >= 40
          );
          allProjects = allProjects.concat(filteredGroupProjects);

          const totalGroupProjectPages = parseInt(groupProjectsResponse.headers["x-total-pages"] || "1", 10);
          if (groupProjectsPage >= totalGroupProjectPages) {
            break;
          }
          groupProjectsPage++;
        }
      }

      const uniqueProjects = removeDuplicateProjects(allProjects);

      return { success: true, projects: uniqueProjects };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  const getGroupProjects = async (groupId: string): Promise<GitLabIntegrationResponse> => {
    try {
      let allProjects: any[] = [];
      let page = 1;
      const perPage = 100;

      while (true) {
        const response = await axiosInstance.get(`/groups/${groupId}/projects`, {
          params: {
            per_page: perPage,
            page,
            archived: false,
            sort: 'asc',
            order_by: 'name',
            simple: false,
            include_subgroups: true,
          },
        });

        const projects = response.data;
        // const filteredProjects = projects.filter((project: any) =>
        //   project.permissions?.project_access?.access_level >= 40
        // );
        allProjects = allProjects.concat(projects);

        const totalPages = parseInt(response.headers["x-total-pages"] || "1", 10);
        if (page >= totalPages) {
          break;
        }
        page++;
      }

      return {
        success: true,
        projects: allProjects.map((project) => ({
          name: project.name,
          html_url: project.web_url,
          private: project.visibility === 'private',
          description: project.description,
          created_at: project.created_at,
          last_activity_at: project.last_activity_at,
          pushed_at: project.last_activity_at,
          stargazers_count: project.star_count || 0,
          forks_count: project.forks_count || 0,
          open_issues_count: project.open_issues_count || 0,
          watchers_count: project.star_count || 0,
          language: project.language || null,
          default_branch: project.default_branch,
          is_repo: true,
          repo_name: project.name,
          web_url: project.web_url,
          platform: 'GitLab',
          visibility: project.visibility,
          size: project.size || 0,
          star_count: project.star_count || 0,
          watchers: project.star_count || 0,
          forks: project.forks_count || 0,
          open_issues: project.open_issues_count || 0,
          namespace: {
            name: project.namespace?.name,
            full_path: project.namespace?.full_path,
            id: project.namespace?.id,
            web_url: project.namespace?.web_url,
            kind: project.namespace?.kind,
          },
          owner: {
            login: project.namespace?.name || '',
            id: project.namespace?.id || 0,
            html_url: project.namespace?.web_url || '',
            type: project.namespace?.kind || 'group',
          },
        })),
      };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  const removeDuplicateProjects = (projects: any[]): any[] => {
    const uniqueProjects = new Map();
    for (const project of projects) {
      if (!uniqueProjects.has(project.id)) {
        uniqueProjects.set(project.id, project);
      }
    }
    return Array.from(uniqueProjects.values());
  };

  const checkForNewProjects = async (
    existingProjects: Project[] = [],
    lastCheckTimestamp?: string
  ): Promise<CheckProjectsResponse> => {
    try {
      let allProjects: any[] = [];
      let page = 1;
      const perPage = 100;

      const params: any = {
        per_page: perPage,
        membership: true,
        archived: false,
        sort: 'asc',
        order_by: 'name',
        simple: false,
        statistics: false,
        with_custom_attributes: false,
      };

      if (lastCheckTimestamp) {
        params.updated_after = lastCheckTimestamp;
      }

      while (true) {
        const projectsResponse = await axiosInstance.get('/projects', {
          params: {
            ...params,
            page: page,
          },
        });
        let projects = projectsResponse.data;
        projects = projects.filter((project: any) =>
          project.permissions?.project_access?.access_level >= 40
        );
        allProjects = allProjects.concat(projects);

        const totalPages = parseInt(projectsResponse.headers['x-total-pages'] || '1', 10);
        if (page >= totalPages) {
          break;
        }
        page++;
      }

      let groupsPage = 1;
      const groups: any[] = [];

      while (true) {
        const groupsResponse = await axiosInstance.get('/groups', {
          params: {
            per_page: perPage,
            page: groupsPage,
            min_access_level: 40,
            all_available: false,
          },
        });

        const fetchedGroups = groupsResponse.data;
        groups.push(...fetchedGroups);

        const totalGroupPages = parseInt(groupsResponse.headers['x-total-pages'] || '1', 10);
        if (groupsPage >= totalGroupPages) {
          break;
        }
        groupsPage++;
      }

      for (const group of groups) {
        let groupProjectsPage = 1;

        while (true) {
          const groupProjectsResponse = await axiosInstance.get(`/groups/${group.id}/projects`, {
            params: {
              per_page: perPage,
              page: groupProjectsPage,
              archived: false,
              sort: 'asc',
              order_by: 'name',
              simple: false,
              include_subgroups: true,
            },
          });

          const groupProjects = groupProjectsResponse.data;
          const filteredGroupProjects = groupProjects.filter((project: any) =>
            project.permissions?.project_access?.access_level >= 40
          );
          allProjects = allProjects.concat(filteredGroupProjects);

          const totalGroupProjectPages = parseInt(
            groupProjectsResponse.headers['x-total-pages'] || '1',
            10
          );
          if (groupProjectsPage >= totalGroupProjectPages) {
            break;
          }
          groupProjectsPage++;
        }
      }

      const uniqueProjects = removeDuplicateProjects(allProjects);

      const existingProjectUrls = new Set(existingProjects.map((project) => project?.web_url));

      const newProjects = uniqueProjects.filter(
        (project) => !existingProjectUrls.has(project.web_url)
      );

      return {
        success: true,
        newProjects: newProjects.map((project) => ({
          name: project.name,
          html_url: project.web_url,
          private: project.visibility === 'private',
          description: project.description,
          created_at: project.created_at,
          last_activity_at: project.last_activity_at,
          pushed_at: project.last_activity_at,
          stargazers_count: project.star_count || 0,
          forks_count: project.forks_count || 0,
          open_issues_count: project.open_issues_count || 0,
          watchers_count: project.star_count || 0,
          language: project.language || null,
          default_branch: project.default_branch,
          is_repo: true,
          repo_name: project.name,
          web_url: project.web_url,
          platform: 'GitLab',
          visibility: project.visibility,
          size: project.size || 0,
          star_count: project.star_count || 0,
          watchers: project.star_count || 0,
          forks: project.forks_count || 0,
          open_issues: project.open_issues_count || 0,
          namespace: {
            name: project.namespace?.name,
            full_path: project.namespace?.full_path,
            id: project.namespace?.id,
            web_url: project.namespace?.web_url,
            kind: project.namespace?.kind,
          },
          owner: {
            login: project.namespace?.name || '',
            id: project.namespace?.id || 0,
            html_url: project.namespace?.web_url || '',
            type: project.namespace?.kind || 'group',
          },
        })),
      };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  const runChecks = async (projectPath: string) => {
    const results = {
      vulnerabilities: await checkVulnerabilities(projectPath),
      branchProtection: await checkBranchProtection(projectPath),
      codeReviewRequirements: await checkCodeReviewRequirements(projectPath),
    };
    return results;
  };

  const saveGitLabIntegration = async (data: any, repos: any[], groups: any[], currentUser: any, group2FAResults: any[]) => {
    try {
      let isAnyRepoAdded = false;
      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, '0')}`;
      let integrationId: string | undefined;

      if (!data.tenantId || typeof data.tenantId !== 'string' || data.tenantId.trim() === '') {
        throw new Error('Invalid tenantId provided');
      }

      const tenantRef = doc(db, 'tenants', data.tenantId);
      const gitlabRef = collection(tenantRef, 'integrations');
      const vendorsRef = collection(tenantRef, 'vendors');
      const integrationSnapshot = await getDocs(
        query(gitlabRef, where('name', '==', 'GitLab'))
      );

      if (integrationSnapshot.empty) {
        const newData = {
          accessToken: data.accessToken,
          name: 'GitLab',
          group: 'Developer Tools',
          img_path: '/integrations/Gitlab.svg',
          status: 'Connected',
          connectedAt: formattedDate,
          tenantId: data.tenantId,
          uid: data.uid,
          createdAt: serverTimestamp(),
          group2FAResults,
          groups,
        };

        const docRef = await addDoc(gitlabRef, newData);
        integrationId = docRef.id;

        const vendorData = {
          authentication_type: '',
          category: 'Developer Tools',
          created_at: serverTimestamp(),
          due_deligence_status: 'Incomplete',
          impact_assessment: [],
          logo: [{
            name: 'gitlab_logo.png',
            size: 3395,
            url: '/integrations/gitlab.svg',
          }],
          owner: currentUser?.userName || '',
          policy_url: 'https://about.gitlab.com/privacy/',
          reliance: '',
          risk_level: '',
          tenantId: data.tenantId,
          terms_url: 'https://about.gitlab.com/terms/',
          two_factor: true,
          uid: data.uid,
          updated_at: serverTimestamp(),
          vendor_name: 'GitLab',
          website_url: 'https://gitlab.com',
          group2FAResults,
        };

        await addDoc(vendorsRef, vendorData);
      } else {
        integrationId = integrationSnapshot.docs[0].id;
      }

      return { success: true, reposAdded: isAnyRepoAdded, integrationId };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  return {
    runChecks,
    getAllGitLabProjects,
    getGroupProjects,
    checkUser2FA,
    checkVulnerabilities,
    checkBranchProtection,
    checkCodeReviewRequirements,
    checkForNewProjects,
    saveGitLabIntegration,
  };
};

export const exchangeCodeForGitlabToken = async (
  code: string,
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void
) => {
  let toastId;
  try {
    toastId = showLoadingToast({ message: 'Authenticating with GitLab...' });
    const tokenResponse = await fetch(
      `https://us-central1-slate-134a0.cloudfunctions.net/gitlabOAuthCallback?code=${encodeURIComponent(code)}`,
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          Accept: 'application/json',
        },
      }
    );

    const responseText = await tokenResponse.text();

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      throw new Error('Invalid response format from server');
    }

    if (!data.access_token || typeof data.access_token !== 'string' || data.access_token.trim() === '') {
      throw new Error('No valid access token received from server');
    }

    updateLoadingToast(toastId, {
      message: 'Token received, fetching repositories...',
      type: 'info',
      isLoading: true,
    });

    localStorage.setItem('gitlab_access_token', data.access_token);

    const integrationId = await fetchAndSaveGitlabRepositories(
      data.access_token,
      data.groups,
      currentUser,
      navigate
    );

    dismissLoadingToast(toastId);
    showSuccessToast({ message: 'Successfully authenticated with GitLab' });

    navigate(`/integrations?id=${integrationId}&service=GitLab`, { replace: true });
  } catch (error: any) {
    showErrorToast({ message: 'GitLab authentication failed. Try again!' });
    dismissLoadingToast(toastId);
    localStorage.removeItem('gitlab_access_token');
    navigate('/integrations', { replace: true });
    throw error;
  }
};

export const fetchAndSaveGitlabRepositories = async (
  accessToken: string,
  groups: any[],
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void
): Promise<string | undefined> => {
  try {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, '0')}`;
    let integrationId: string | undefined;

    const tenantRef = doc(db, 'tenants', currentUser?.tenantId);
    const gitlabRef = collection(tenantRef, 'integrations');
    const vendorsRef = collection(tenantRef, 'vendors');
    const integrationSnapshot = await getDocs(
      query(gitlabRef, where('name', '==', 'GitLab'))
    );

    if (integrationSnapshot.empty) {
      const newData = {
        accessToken: accessToken,
        name: 'GitLab',
        group: 'Developer Tools',
        img_path: '/integrations/Gitlab.svg',
        status: 'Connected',
        connectedAt: formattedDate,
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        createdAt: serverTimestamp(),
        // group2FAResults,
        groups,
      };

      const docRef = await addDoc(gitlabRef, newData);
      integrationId = docRef.id;

      const vendorData = {
        authentication_type: '',
        category: 'Developer Tools',
        created_at: serverTimestamp(),
        due_deligence_status: 'Incomplete',
        impact_assessment: [],
        logo: [{
          name: 'gitlab_logo.png',
          size: 3395,
          url: '/integrations/gitlab.svg',
        }],
        owner: currentUser?.userName || '',
        policy_url: 'https://about.gitlab.com/privacy/',
        reliance: '',
        risk_level: '',
        tenantId: currentUser?.tenantId,
        terms_url: 'https://about.gitlab.com/terms/',
        two_factor: true,
        uid: currentUser?.uid,
        updated_at: serverTimestamp(),
        vendor_name: 'GitLab',
        website_url: 'https://gitlab.com',
        // group2FAResults,
      };

      await addDoc(vendorsRef, vendorData);
    } else {
      integrationId = integrationSnapshot.docs[0].id;
    }

    return integrationId;
  } catch (error) {
    showErrorToast({ message: 'Error setting up GitLab integration' });
    navigate('/integrations', { replace: true });
  }
};